/**
 * SchedulePublishModal Component
 *
 * Modal for scheduling campaigns/organic posts for future publishing.
 * For organic posts: platform-first selection (one at a time), then accounts,
 * then which ad variations to publish. For paid ads: multi-platform as before.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { apiClient } from '../../services/api';
import { useToast } from '../ui/Toast';
import { TIME_SLOTS, toSlot } from '../../lib/publishSlots';
import { OAuthApiClient } from '../../lib/oauth/OAuthApiClient';
import { PlatformLogo, getPlatformName } from '../icons/PlatformLogos';
import type { PlatformKey } from '../icons/PlatformLogos';
import type { EnhancedOAuthConnection, Platform } from '../../lib/oauth/types';

export interface ScheduleResult {
  id: string;
  status: string;
  scheduled_at: string;
  publish_type: string;
  campaign_title?: string;
}

interface SchedulePublishModalProps {
  isOpen: boolean;
  onClose: () => void;
  conversationId: string;
  campaignTitle: string;
  campaignConfig: any;
  platforms: Platform[];
  publishType: 'paid_ad' | 'organic_post';
  onScheduleComplete?: (result: ScheduleResult) => void;
  conversationMedia?: Array<{ filename: string; url: string; type: string }>;
  resolvedMedia?: Array<{ filename: string; url: string; media_type: string }>;
}

/** Extract ad variations for a given platform from the campaign config. */
function getVariationsForPlatform(
  campaignConfig: any,
  platform: string
): Array<{ index: number; label: string }> {
  const extractFromSets = (sets: any[]): any[] => {
    const vars: any[] = [];
    for (const set of sets || []) {
      vars.push(...(set.ad_variations || set.ads || []));
    }
    return vars;
  };

  let variations: any[] = [];

  // Canonical format: { campaigns: [{platform, platform_specific}] }
  if (campaignConfig?.campaigns) {
    const match = campaignConfig.campaigns.find((c: any) => c.platform === platform);
    const ps = match?.platform_specific || {};
    variations = extractFromSets(ps.ad_sets || ps.ad_groups || ps.line_items || []);
  }

  // Legacy platform-keyed format: { meta: {ad_sets: [...]}, ... }
  if (!variations.length) {
    const pc = campaignConfig?.[platform] || campaignConfig;
    variations = extractFromSets(pc?.ad_sets || pc?.ad_groups || pc?.line_items || []);
  }

  return variations.map((v, i) => ({
    index: i,
    label: [v.headline, v.primary_text, v.title]
      .filter(Boolean)
      .map((s: string) => s.slice(0, 50))
      .join(' · ')
      .trim() || `Variation ${i + 1}`,
  }));
}

/** Platforms supported for organic publishing and their OAuth key. */
const ORGANIC_PLATFORMS: { key: string; label: string }[] = [
  { key: 'meta', label: 'Meta (Facebook / Instagram)' },
  { key: 'twitter', label: 'X / Twitter' },
  { key: 'pinterest', label: 'Pinterest' },
];

export const SchedulePublishModal: React.FC<SchedulePublishModalProps> = ({
  isOpen,
  onClose,
  conversationId,
  campaignTitle,
  campaignConfig,
  platforms,
  publishType,
  onScheduleComplete,
}) => {
  const { currentOrganization, user } = useAuth();
  const { toast } = useToast();
  const isOrganic = publishType === 'organic_post';

  // Connection state
  const [connections, setConnections] = useState<EnhancedOAuthConnection[]>([]);
  const [selectedConnections, setSelectedConnections] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [scheduling, setScheduling] = useState(false);

  // Organic-only: single platform selection
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);

  // Organic-only: variation selection
  const [selectedVariations, setSelectedVariations] = useState<Set<number>>(new Set());

  // Schedule state
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  );

  // Step state
  const [currentStep, setCurrentStep] = useState<'configure' | 'scheduling' | 'complete'>('configure');
  const [scheduleResult, setScheduleResult] = useState<ScheduleResult | null>(null);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      setScheduledDate(tomorrow.toISOString().split('T')[0]);
      setScheduledTime(toSlot('09:00'));
      setCurrentStep('configure');
      setScheduleResult(null);
      setSelectedPlatform(null);
      setSelectedVariations(new Set());
      setSelectedConnections(new Set());
    }
  }, [isOpen]);

  // Load OAuth connections
  const loadConnections = useCallback(async () => {
    if (!currentOrganization?.id) return;
    setLoading(true);
    try {
      const oauthClient = new OAuthApiClient();
      const conns = await oauthClient.getConnectionsEnhanced();
      setConnections(conns);
    } catch (err) {
      console.error('Failed to load connections:', err);
    } finally {
      setLoading(false);
    }
  }, [currentOrganization?.id]);

  useEffect(() => {
    if (isOpen) loadConnections();
  }, [isOpen, loadConnections]);

  // ── Organic: platforms that have at least one connected account ──────────
  const availableOrganicPlatforms = useMemo(() => {
    return ORGANIC_PLATFORMS.filter((p) =>
      connections.some((c) => c.platform === p.key)
    );
  }, [connections]);

  // ── Connections for the selected platform (organic) or all platforms (paid) ──
  const relevantConnections = useMemo(() => {
    if (isOrganic) {
      return selectedPlatform
        ? connections.filter((c) => c.platform === selectedPlatform)
        : [];
    }
    return connections.filter((c) => platforms.includes(c.platform as Platform));
  }, [connections, isOrganic, selectedPlatform, platforms]);

  // Group by platform (for paid ads multi-platform display)
  const connectionsByPlatform = useMemo(() =>
    relevantConnections.reduce<Record<string, EnhancedOAuthConnection[]>>((acc, conn) => {
      acc[conn.platform] = [...(acc[conn.platform] || []), conn];
      return acc;
    }, {}),
  [relevantConnections]);

  // ── Variations for the selected platform ────────────────────────────────
  const variations = useMemo(() => {
    if (!isOrganic || !selectedPlatform) return [];
    return getVariationsForPlatform(campaignConfig, selectedPlatform);
  }, [isOrganic, selectedPlatform, campaignConfig]);

  // Reset connections + variations when platform changes
  useEffect(() => {
    setSelectedConnections(new Set());
    setSelectedVariations(new Set([0])); // default: first variation
  }, [selectedPlatform]);

  const toggleConnection = (id: string) => {
    setSelectedConnections((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleVariation = (idx: number) => {
    setSelectedVariations((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const selectedCount = selectedConnections.size;

  const getDisplayTime = () => {
    if (!scheduledDate || !scheduledTime) return '';
    try {
      return new Date(`${scheduledDate}T${scheduledTime}:00`).toLocaleString('en-US', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
        hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
      });
    } catch { return ''; }
  };

  const canSchedule = useMemo(() => {
    if (!scheduledDate || !scheduledTime) return false;
    if (selectedCount === 0) return false;
    if (isOrganic && !selectedPlatform) return false;
    if (isOrganic && variations.length > 0 && selectedVariations.size === 0) return false;
    return true;
  }, [scheduledDate, scheduledTime, selectedCount, isOrganic, selectedPlatform, variations, selectedVariations]);

  const handleSchedule = async () => {
    if (!user || !conversationId || !canSchedule) return;

    const localDatetime = `${scheduledDate}T${scheduledTime}:00`;
    const scheduledDt = new Date(localDatetime);

    if (scheduledDt <= new Date()) {
      toast.warning('Scheduled time must be in the future.');
      return;
    }

    setScheduling(true);
    setCurrentStep('scheduling');

    try {
      const connectionsList = Array.from(selectedConnections).map((connId) => {
        const conn = connections.find((c) => c.id === connId);
        return {
          connection_id: connId,
          account_id: conn?.platform_account_id || conn?.id || '',
          platform: conn?.platform || '',
          account_name: conn?.account_name || '',
        };
      });

      const payload: any = {
        conversation_id: conversationId,
        scheduled_at: scheduledDt.toISOString(),
        timezone,
        publish_type: publishType,
        campaign_config: campaignConfig,
        campaign_title: campaignTitle,
        connections: connectionsList,
        user_id: user.id,
      };

      // For organic posts, pass variation_indices if the user selected specific ones
      if (isOrganic && variations.length > 0) {
        payload.variation_indices = Array.from(selectedVariations).sort((a, b) => a - b);
      }

      const response = await apiClient.post('/api/v1/scheduler/schedule', payload);
      setScheduleResult(response.data);
      setCurrentStep('complete');
      toast.success('Campaign scheduled successfully!');
      if (onScheduleComplete) onScheduleComplete(response.data);
    } catch (err: any) {
      console.error('Schedule failed:', err);
      toast.error(err?.response?.data?.detail || err?.message || 'Failed to schedule campaign');
      setCurrentStep('configure');
    } finally {
      setScheduling(false);
    }
  };

  const handleClose = () => {
    setCurrentStep('configure');
    setScheduleResult(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg mx-4 bg-card dark:bg-card-dark rounded-xl shadow-2xl border border-line dark:border-border-dark overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-line dark:border-border-dark">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h2 className="text-lg font-semibold text-ink">
                Schedule {isOrganic ? 'Posts' : 'Campaign'}
              </h2>
            </div>
            <button onClick={handleClose} className="p-1 text-ink-3 hover:text-ink-2 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {campaignTitle && (
            <p className="mt-1 text-sm text-ink-3 truncate">{campaignTitle}</p>
          )}
        </div>

        {/* Body */}
        <div className="px-6 py-4 max-h-[65vh] overflow-y-auto space-y-5">
          {currentStep === 'configure' && (
            <>
              {/* Date & Time */}
              <div className="p-4 rounded-lg bg-work-soft border border-work/30">
                <h3 className="text-sm font-semibold text-work mb-3">
                  Schedule Date & Time
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-ink-2 mb-1">Date</label>
                    <input
                      type="date"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 text-sm rounded-lg border border-line-2 bg-card text-ink focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-ink-2 mb-1">Time</label>
                    {/* Publish slots only (lib/publishSlots): the time shown is when it goes out */}
                    <select
                      value={toSlot(scheduledTime)}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      className="w-full px-3 py-2 text-sm rounded-lg border border-line-2 bg-card text-ink focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-xs font-medium text-ink-2 mb-1">Timezone</label>
                  <select
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-line-2 bg-card text-ink focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    {COMMON_TIMEZONES.map((tz) => (
                      <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                </div>
                {scheduledDate && scheduledTime && (
                  <p className="mt-3 text-xs text-primary">
                    Will publish on {getDisplayTime()}
                  </p>
                )}
              </div>

              {/* Platform selector — organic only */}
              {isOrganic && (
                <div>
                  <h3 className="text-sm font-semibold text-ink-2 mb-2">
                    Select Platform
                  </h3>
                  {loading ? (
                    <div className="flex items-center gap-2 py-3 text-sm text-ink-3">
                      <div className="w-4 h-4 border-2 border-work-dot border-t-transparent rounded-full animate-spin" />
                      Loading...
                    </div>
                  ) : availableOrganicPlatforms.length === 0 ? (
                    <p className="text-sm text-ink-3">No connected accounts found.</p>
                  ) : (
                    <div className="space-y-2">
                      {availableOrganicPlatforms.map(({ key, label }) => (
                        <label
                          key={key}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer border transition-colors
                            ${selectedPlatform === key
                              ? 'bg-work-soft border-work/40'
                              : 'bg-card border-line dark:border-border-dark hover:bg-ground'
                            }`}
                        >
                          <input
                            type="radio"
                            name="platform"
                            checked={selectedPlatform === key}
                            onChange={() => setSelectedPlatform(key)}
                            className="w-4 h-4 text-work border-line-2 focus:ring-primary"
                          />
                          <PlatformLogo platform={key as PlatformKey} size="sm" />
                          <span className="text-sm font-medium text-ink">{label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Account selection */}
              {(!isOrganic || selectedPlatform) && (
                <div>
                  <h3 className="text-sm font-semibold text-ink-2 mb-2">
                    Select Accounts
                  </h3>
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-6 h-6 border-2 border-work-dot border-t-transparent rounded-full animate-spin" />
                      <span className="ml-2 text-sm text-ink-3">Loading accounts...</span>
                    </div>
                  ) : relevantConnections.length === 0 ? (
                    <p className="py-4 text-center text-sm text-ink-3">
                      No connected accounts for this platform.
                    </p>
                  ) : isOrganic ? (
                    // Organic: flat list (single platform already selected)
                    <div className="space-y-1">
                      {relevantConnections.map((conn) => (
                        <label
                          key={conn.id}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer border transition-colors
                            ${selectedConnections.has(conn.id)
                              ? 'bg-work-soft border-work/40'
                              : 'bg-card border-line dark:border-border-dark hover:bg-ground'
                            }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedConnections.has(conn.id)}
                            onChange={() => toggleConnection(conn.id)}
                            className="w-4 h-4 text-work rounded border-line-2 focus:ring-primary"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-ink truncate">
                              {conn.account_name || 'Account'}
                            </div>
                            {conn.platform_account_id && (
                              <div className="text-xs text-ink-3 truncate">ID: {conn.platform_account_id}</div>
                            )}
                          </div>
                          {conn.status === 'valid' && (
                            <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
                          )}
                        </label>
                      ))}
                    </div>
                  ) : (
                    // Paid ads: grouped by platform
                    <div className="space-y-3">
                      {Object.entries(connectionsByPlatform).map(([platform, conns]) => (
                        <div key={platform}>
                          <div className="flex items-center gap-2 mb-1.5">
                            <PlatformLogo platform={platform as PlatformKey} size="sm" />
                            <span className="text-xs font-semibold text-ink-3 uppercase tracking-wide">
                              {getPlatformName(platform as PlatformKey)}
                            </span>
                          </div>
                          <div className="space-y-1">
                            {conns.map((conn) => (
                              <label
                                key={conn.id}
                                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer border transition-colors
                                  ${selectedConnections.has(conn.id)
                                    ? 'bg-work-soft border-work/40'
                                    : 'bg-card border-line dark:border-border-dark hover:bg-ground'
                                  }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedConnections.has(conn.id)}
                                  onChange={() => toggleConnection(conn.id)}
                                  className="w-4 h-4 text-work rounded border-line-2 focus:ring-primary"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-ink truncate">
                                    {conn.account_name || 'Account'}
                                  </div>
                                  {conn.platform_account_id && (
                                    <div className="text-xs text-ink-3 truncate">ID: {conn.platform_account_id}</div>
                                  )}
                                </div>
                                {conn.status === 'valid' && (
                                  <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
                                )}
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Variation selector — organic only, after platform & account selected */}
              {isOrganic && selectedPlatform && selectedCount > 0 && variations.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-ink-2 mb-1">
                    Select Variations
                  </h3>
                  <p className="text-xs text-ink-3 mb-2">
                    Choose which ad variations to publish. You can schedule others separately.
                  </p>
                  <div className="space-y-1">
                    {variations.map(({ index, label }) => (
                      <label
                        key={index}
                        className={`flex items-start gap-3 px-3 py-2.5 rounded-lg cursor-pointer border transition-colors
                          ${selectedVariations.has(index)
                            ? 'bg-work-soft border-work/40'
                            : 'bg-card border-line dark:border-border-dark hover:bg-ground'
                          }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedVariations.has(index)}
                          onChange={() => toggleVariation(index)}
                          className="mt-0.5 w-4 h-4 text-work rounded border-line-2 focus:ring-primary"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-ink-3 mb-0.5">
                            Variation {index + 1}
                          </div>
                          <div className="text-sm text-ink truncate">{label}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {currentStep === 'scheduling' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-10 h-10 border-3 border-work-dot border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-sm font-medium text-ink-2">
                Scheduling your {isOrganic ? 'posts' : 'campaign'}...
              </p>
            </div>
          )}

          {currentStep === 'complete' && scheduleResult && (
            <div className="flex flex-col items-center py-8">
              <div className="w-12 h-12 rounded-full bg-ok-soft flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-ok" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-ink mb-1">Scheduled!</h3>
              <p className="text-sm text-ink-3 text-center">
                Your {isOrganic ? 'posts' : 'campaign'} will be published on
              </p>
              <p className="text-sm font-medium text-primary mt-1">{getDisplayTime()}</p>
              <p className="text-xs text-ink-3 mt-2">
                to {selectedCount} account{selectedCount !== 1 ? 's' : ''}
                {isOrganic && selectedVariations.size > 0 && ` · ${selectedVariations.size} variation${selectedVariations.size !== 1 ? 's' : ''}`}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-line dark:border-border-dark flex justify-end gap-3">
          {currentStep === 'configure' && (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-ink-2 bg-inset rounded-lg hover:bg-inset transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSchedule}
                disabled={!canSchedule}
                className="px-5 py-2 text-sm font-semibold text-white bg-gradient-to-r from-primary to-primary-dark rounded-lg hover:from-primary-dark hover:to-primary-dark shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Schedule
                {selectedCount > 0 && (
                  <span className="ml-0.5">
                    {isOrganic && selectedVariations.size > 0
                      ? `${selectedVariations.size} var · ${selectedCount} acct`
                      : `${selectedCount} account${selectedCount !== 1 ? 's' : ''}`}
                  </span>
                )}
              </button>
            </>
          )}
          {currentStep === 'complete' && (
            <button
              onClick={handleClose}
              className="px-5 py-2 text-sm font-medium text-white bg-gradient-to-r from-primary to-primary-dark rounded-lg hover:from-primary-dark hover:to-primary-dark transition-all"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const COMMON_TIMEZONES = [
  'UTC',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Anchorage', 'Pacific/Honolulu', 'America/Toronto', 'America/Vancouver',
  'America/Sao_Paulo', 'America/Argentina/Buenos_Aires',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Moscow',
  'Asia/Dubai', 'Asia/Kolkata', 'Asia/Shanghai', 'Asia/Tokyo',
  'Asia/Seoul', 'Asia/Singapore',
  'Australia/Sydney', 'Australia/Melbourne', 'Pacific/Auckland',
];

export default SchedulePublishModal;
